import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

/**
 * 加载用户头像图片
 * 支持多种数据源：
 * 1. Firebase Storage 路径
 * 2. HTTP URL
 * 3. 本地资源路径
 * 
 * @param {Object} profileData - 用户档案数据
 * @param {string} profileData.profileImagePath - 主要头像路径（优先使用）
 * @param {string} profileData.photoURL - 备用头像URL
 * @returns {Promise<string|null>} 返回最终的图片URL，如果无法获取则返回null
 */
export const loadProfileImage = async (profileData) => {
  if (!profileData) return null;
  
  let finalImageUrl = null;
  
  try {
    // 首先检查 profileImagePath（Firebase Storage 路径）
    if (profileData.profileImagePath) {
      if (profileData.profileImagePath.startsWith('http')) {
        // 直接的 HTTP URL
        finalImageUrl = profileData.profileImagePath;
      } else {
        // 尝试从 Firebase Storage 获取
        try {
          const storagePath = profileData.profileImagePath.startsWith('userProfilePhoto/')
            ? profileData.profileImagePath
            : `userProfilePhoto/${profileData.profileImagePath}`;
          
          const imageRef = ref(storage, storagePath);
          finalImageUrl = await getDownloadURL(imageRef);
        } catch (storageError) {
          console.log('Storage error, trying direct path:', storageError);
          // 如果存储错误，尝试直接使用 profileImagePath 作为本地路径
          if (profileData.profileImagePath && !profileData.profileImagePath.startsWith('profile_images/')) {
            finalImageUrl = `/asset/${profileData.profileImagePath}`;
          }
        }
      }
    }
    
    // 如果没有 profileImagePath，尝试 photoURL
    if (!finalImageUrl && profileData.photoURL) {
      if (profileData.photoURL.startsWith('http')) {
        finalImageUrl = profileData.photoURL;
      } else {
        finalImageUrl = `/asset/${profileData.photoURL}`;
      }
    }
    
    return finalImageUrl;
  } catch (error) {
    console.log('Error loading profile image:', error);
    return null;
  }
};

/**
 * 获取默认头像路径
 * @returns {string} 默认头像的路径
 */
export const getDefaultAvatarUrl = () => {
  return '/asset/images/default-avatar.png';
};

/**
 * 获取头像URL，如果没有则返回默认头像
 * @param {Object} profileData - 用户档案数据
 * @returns {Promise<string>} 返回头像URL或默认头像
 */
export const getProfileImageWithFallback = async (profileData) => {
  const imageUrl = await loadProfileImage(profileData);
  return imageUrl || getDefaultAvatarUrl();
};

/**
 * 验证图片URL是否有效
 * @param {string} url - 图片URL
 * @returns {Promise<boolean>} 返回URL是否有效
 */
export const validateImageUrl = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * 预加载头像图片
 * @param {string} url - 图片URL
 * @returns {Promise<void>} 预加载完成的Promise
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}; 